"use client"

import React, { useState } from "react"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Orbit } from "lucide-react"
import { EntityBrowser, ReferenceTypeBrowser } from "@/components/shared/entity-browser"
import { useExplorerContext } from "@/components/explorer/explorer-context"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export type RailTab = "entities" | "reference"

/**
 * The explorer's left rail: the grouped entity browser and the reference-data sets as
 * two tabs, so the rail stays scannable on tenants with hundreds of entities.
 */
export function EntityRail({ onOpenMap }: { onOpenMap?: () => void }) {
  const { entityTypes, isLoadingTypes, selectedEntity, selectEntity, reference } = useExplorerContext()
  const { t, n } = useTranslation()
  const [tab, setTab] = useState<RailTab>("entities")

  return (
    <aside className="flex h-full w-[280px] shrink-0 flex-col border-r border-card-border bg-card">
      <Tabs value={tab} onValueChange={(value) => setTab(value as RailTab)} className="flex min-h-0 flex-1 flex-col">
        <TabsList className="m-3 mb-0 grid grid-cols-2 rounded-lg bg-muted p-0.5">
          <TabsTrigger value="entities" className="text-xs">
            {t("explorer.rail.entities")}
            {entityTypes.length > 0 ? ` · ${n(entityTypes.length)}` : ""}
          </TabsTrigger>
          <TabsTrigger value="reference" className="text-xs" disabled={reference.types.length === 0}>
            {t("explorer.rail.reference")}
            {reference.types.length > 0 ? ` · ${n(reference.types.length)}` : ""}
          </TabsTrigger>
        </TabsList>
        {onOpenMap && (
          <button
            type="button"
            onClick={onOpenMap}
            className="mx-3 mt-2 inline-flex items-center justify-center gap-1.5 rounded-[9px] border border-control-border bg-card px-3 py-1.5 text-xs font-medium text-[var(--gw-text-body)] hover:border-[var(--th-control-border-hover)]"
            title={t("explorer.rail.entityMapTitle")}
          >
            <Orbit className="h-3.5 w-3.5" /> {t("explorer.entityMap.title")}
          </button>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {tab === "entities" ? (
            <EntityBrowser entities={entityTypes} selectedName={selectedEntity?.name ?? null} onSelect={selectEntity} isLoading={isLoadingTypes} />
          ) : (
            <ReferenceTypeBrowser referenceTypes={reference.types} selectedType={reference.selectedType} onSelect={reference.select} />
          )}
        </div>
      </Tabs>
    </aside>
  )
}
